import * as React from 'react'
import { To, useLocation, useNavigate } from 'react-router-dom'

import { useDialogState } from '../../components'
import {
  DateRangeType,
  FREE_PERIODS,
  SpecificChartFunctionality,
  TIMEFRAME,
  TimeframeAndIntradayType,
} from '../../constants/common'
import Chart from '../../models/chart'
import {
  CUSTOM_DATE_RANGE,
  CustomDateRangePrefixEnum,
  DATE_RANGE_PREFIX_DELMITER,
} from '../../utils/chart-date-range-utils'
import { decodeQueryString, encodeQueryString } from '../../utils/query_string'
import { useChartsPathLocation } from './use-charts-path-location'
import { getDateRangeGroups, getTimeFrameGroups } from './utils'

export function useTimeframeDateRangeGroups({
  chartModel,
  isPremium,
  specificChartFunctionality,
}: {
  chartModel: Chart | null
  isPremium: boolean
  specificChartFunctionality: SpecificChartFunctionality
}) {
  const location = useLocation()
  const navigate = useNavigate()
  const customDateRangeDialogState = useDialogState()
  const chartLayoutModel = React.useMemo(() => chartModel?.chart_layout() ?? null, [chartModel])
  const chartIndex = chartLayoutModel?.getAllCharts().findIndex((chart) => chartModel!.eql(chart)) ?? -1
  const { getChartsToPath } = useChartsPathLocation({ chartLayoutModel, chartIndex })
  const generateChartLayoutRouterPath = React.useCallback(
    ({
      ticker,
      timeframe,
      dateRange,
    }: {
      timeframe: TimeframeAndIntradayType
      dateRange?: string | null
      ticker?: string
    }) => {
      if (specificChartFunctionality === SpecificChartFunctionality.quotePage) {
        if (isPremium || FREE_PERIODS.includes(timeframe)) {
          return {
            pathname: location.pathname,
            search: `?${encodeQueryString({
              ...decodeQueryString(),
              i: undefined,
              p: timeframe,
              r: dateRange ?? undefined,
            })}`,
          }
        }

        return {
          pathname: '/elite.ashx',
          search: `?${encodeQueryString({
            utm_source: 'finviz',
            utm_medium: 'banner',
            utm_campaign: 'quote-timeframe',
          })}`,
        }
      }

      return getChartsToPath({
        ticker,
        timeframe: timeframe as TIMEFRAME,
        dateRange,
      })
    },
    [specificChartFunctionality, getChartsToPath, isPremium, location.pathname]
  )

  const timeFrameGroups = React.useMemo(
    () =>
      getTimeFrameGroups(isPremium).map((timeFrameGroup) => ({
        label: timeFrameGroup.label,
        timeframes: timeFrameGroup.timeframes.map((timeFrame) => ({
          ...timeFrame,
          href: generateChartLayoutRouterPath({ timeframe: timeFrame.value, dateRange: null }),
        })),
      })),
    [isPremium, generateChartLayoutRouterPath]
  )

  const dateRangeGroups = React.useMemo(() => {
    const rangeGroups = getDateRangeGroups(isPremium)
    if (isPremium) {
      const dateRange = chartModel?.dateRange
      const [dateRangePrefix] = dateRange?.split(DATE_RANGE_PREFIX_DELMITER) ?? []
      rangeGroups.push({
        dateRanges: [
          {
            value:
              dateRange && Object.values<string>(CustomDateRangePrefixEnum).includes(dateRangePrefix)
                ? dateRange
                : CUSTOM_DATE_RANGE,
            label: 'Custom',
          },
        ],
      })
    }

    return rangeGroups.map((dateRangeGroup) => ({
      label: dateRangeGroup.label,
      dateRanges: dateRangeGroup.dateRanges.map((dateRange) => ({
        ...dateRange,
        href:
          !isPremium || Object.values(DateRangeType).includes(dateRange.value as DateRangeType)
            ? generateChartLayoutRouterPath({ timeframe: dateRange.timeframe!, dateRange: dateRange.value })
            : null,
      })),
    }))
  }, [generateChartLayoutRouterPath, chartModel?.dateRange, isPremium])

  const handleDateRangeSelect = React.useCallback(
    ({ value, timeframe, href }: { value: string; timeframe: TIMEFRAME; href?: To }) => {
      if (!timeframe) {
        if (isPremium) {
          customDateRangeDialogState.show()
        }
        return
      }

      if (!href) {
        navigate(generateChartLayoutRouterPath({ timeframe, dateRange: value }))
      }
    },
    [navigate, customDateRangeDialogState, generateChartLayoutRouterPath, isPremium]
  )

  return {
    timeFrameGroups,
    dateRangeGroups,
    customDateRangeDialogState,
    handleDateRangeSelect,
    generateChartLayoutRouterPath,
  }
}
